import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import BotaoVoltar from '../../../components/BotaoVoltar/BotaoVoltar';
import DataTable, { ColumnDef } from '../../../components/DataTable';
import DataToolbar from '../../../components/DataToolbar/DataToolbar';
import Pagination from '../../../components/Pagination/Pagination';
import { maskCnpj } from '../fundo_liquido/fields';
import { listarPrestadores } from './prestadoresService';
import { PrestadorListItem } from './types';
import PrestadorDetalhe from './detalhe';
import PrestadorCadastro from './PrestadorForm';
import '../fundos_home/style.css';
import '../fundo_liquido/style.css';

const SkBar: React.FC<{ w?: string | number; h?: number }> = ({ w = '100%', h = 12 }) => (
  <span className="fh-sk" style={{ width: typeof w === 'number' ? `${w}px` : w, height: h }} />
);
const SK_WIDTHS = ['55%', '85%', '70%', '40%', '35%'];
const SkeletonTabela: React.FC<{ colunas: ColumnDef<PrestadorListItem>[]; linhas?: number }> = ({ colunas, linhas = 10 }) => (
  <div className="table-container" aria-busy="true" aria-label="Carregando prestadores">
    <table className="fiori-table">
      <thead><tr>{colunas.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
      <tbody>
        {Array.from({ length: linhas }).map((_, r) => (
          <tr key={r} className="table-row">
            {colunas.map((c, i) => <td key={c.key}><SkBar w={SK_WIDTHS[i % SK_WIDTHS.length]} /></td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PrestadoresHome: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const base = '/cadastro/prestador';

  const [prestadores, setPrestadores] = useState<PrestadorListItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [cadastrando, setCadastrando] = useState(false);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(10);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setPrestadores(await listarPrestadores());
    } catch (e: any) {
      setErro(e?.message ?? 'Falha ao carregar os prestadores.');
      setPrestadores([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return prestadores;
    return prestadores.filter((p) =>
      p.nome.toLowerCase().includes(q) ||
      p.grupo_economico.toLowerCase().includes(q) ||
      p.cnpj.includes(q.replace(/\D/g, '')),
    );
  }, [busca, prestadores]);

  const inicio = (pagina - 1) * tamanhoPagina;
  const paginaAtual = filtrados.slice(inicio, inicio + tamanhoPagina);

  const colunas = useMemo((): ColumnDef<PrestadorListItem>[] => [
    { key: 'cnpj', label: 'CNPJ', sortable: true, render: (p) => (p.cnpj ? maskCnpj(p.cnpj) : '—') },
    { key: 'nome', label: 'Nome', sortable: true },
    { key: 'grupo_economico', label: 'Grupo Econômico', sortable: true, render: (p) => p.grupo_economico || '—' },
    {
      key: 'is_anga', label: 'ANGA', sortable: true,
      render: (p) => <span className={`fl-badge ${p.is_anga ? 'fl-badge--on' : 'fl-badge--off'}`}>{p.is_anga ? 'Sim' : 'Não'}</span>,
    },
    { key: 'qtd_fundos', label: 'Fundos', sortable: true },
  ], []);

  const abrir = (p: PrestadorListItem) => navigate(`${base}/${p.id}`);

  if (id) {
    return (
      <PrestadorDetalhe
        prestadorId={Number(id)}
        onVoltar={() => { navigate(base); carregar(); }}
        onExcluido={() => { navigate(base); carregar(); }}
      />
    );
  }

  if (cadastrando) {
    const voltar = () => { setCadastrando(false); carregar(); };
    return (
      <div className="fh-wrap">
        <div className="fl-head">
          <div className="fl-head-left">
            <BotaoVoltar onClick={voltar} />
            <div>
              <h1 className="fl-title">Novo prestador</h1>
              <p className="fl-subtitle">Cadastre uma empresa prestadora de serviço e os papéis que ela pode exercer.</p>
            </div>
          </div>
        </div>
        <PrestadorCadastro onCriado={(novoId) => navigate(`${base}/${novoId}`)} />
      </div>
    );
  }

  return (
    <div className="fh-home">
      <DataToolbar
        title="Prestadores"
        searchValue={busca}
        searchPlaceholder="Buscar por nome, CNPJ, grupo..."
        onSearchChange={(v) => { setBusca(v); setPagina(1); }}
        primaryLabel="Novo"
        primaryIcon={<Plus size={16} />}
        onPrimary={() => setCadastrando(true)}
      />

      {carregando ? (
        <SkeletonTabela colunas={colunas} linhas={tamanhoPagina} />
      ) : (
        <DataTable<PrestadorListItem>
          data={paginaAtual}
          columns={colunas}
          rowKey={(p, i) => `${p.id}-${i}`}
          onRowClick={abrir}
          emptyTitle={erro ? 'Erro ao carregar' : 'Nenhum prestador encontrado'}
          emptyMessage={erro ?? 'Não há prestadores com os filtros aplicados.'}
          emptyHint={erro ? 'Tente novamente em instantes.' : 'Ajuste a busca ou cadastre um novo prestador.'}
        />
      )}

      {!carregando && filtrados.length > 0 && (
        <Pagination
          currentPage={pagina - 1}
          totalPages={Math.ceil(filtrados.length / tamanhoPagina)}
          pageSize={tamanhoPagina}
          totalElements={filtrados.length}
          onPageChange={(p) => setPagina(p + 1)}
          onPageSizeChange={(s) => { setTamanhoPagina(s); setPagina(1); }}
        />
      )}
    </div>
  );
};

export default PrestadoresHome;
